import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import gql from 'graphql-tag';
import map from 'lodash/map';
import take from 'lodash/take';
import filter from 'lodash/filter';
import { Col } from 'react-bootstrap';
import { Alert } from '@canaveral/components';
import { connect } from 'react-redux';
import styles from './styles.scss';
import UnitListingRow from './UnitListingRow';
import SMFacilityLink from '../SMFacilityLink';
import Translation from '../../../../components/stateless/Translation';
import { create as createLead } from '../../../../../src/web/store/modules/lead';
import CreateLeadModal from '../../../../../src/web/containers/UnitsTable/CreateLeadModal';
import { compose } from '../../../../../src/web/utils/react';

const propTypes = {
  facility: PropTypes.shape({
    unitGroups: PropTypes.array().isRequired,
  }).isRequired,
  moveInPath: PropTypes.string.isRequired,
  selectedUnitSize: PropTypes.string,
  selectedAmenities: PropTypes.arrayOf(PropTypes.string),
  multiLocale: PropTypes.bool,
};

const defaultProps = {
  selectedUnitSize: undefined,
  selectedAmenities: [],
  multiLocale: false,
};

const UnitListing = ({
  facility,
  moveInPath,
  selectedUnitSize,
  selectedAmenities,
  multiLocale,
  theme,
  createParkingLead,
}) => {
  const [showCreateLeadModal, setShowCreateLeadModal] = useState(false);
  const [leadConfirmed, setLeadConfirmed] = useState(false);
  const [selectedUnitGroupId, setSelectedUnitGroupId] = useState('');
  const {
    unitGroups,
  } = facility;

  const availableUnitGroups = filter(unitGroups, ug => ug.availableUnitsCount > 0);
  let unitsToShow = availableUnitGroups.sort((a, b) => (
    (a.discountedPrice || a.price) - (b.discountedPrice || b.price)
  ));

  if (selectedUnitSize) {
    if (selectedUnitSize === 'parking') {
      unitsToShow = filter(unitsToShow, ug => ug.categoryName === 'vehicle');
    } else {
      const selectedUnitSizeNumber = parseInt(selectedUnitSize, 10);
      unitsToShow = filter(unitsToShow, ug => (
        ug.area >= selectedUnitSizeNumber * 0.9 && ug.area <= selectedUnitSizeNumber * 1.1
      ));
    }
  }

  if (selectedAmenities.length) {
    unitsToShow = filter(unitsToShow, (ug) => {
      const amenityTokens = map(ug.amenities, amenity => amenity.token);
      return selectedAmenities.every(amenityToken => amenityTokens.some(amenity => amenityToken.split('|').includes(amenity)));
    });
  }

  const toggleCreateLeadModal = (id = '') => {
    setShowCreateLeadModal(!showCreateLeadModal);
    setSelectedUnitGroupId(id);
    setLeadConfirmed(false);
  };

  const handleLeadFormSubmit = (values) => {
    if (values) {
      const {
        firstName,
        lastName,
        email,
        phoneNumber,
        unitGroupId,
        ipAddress,
        googleClientId,
      } = values;

      createParkingLead({
        facilityId: facility.id,
        source: 'no comment',
        tenant: {
          firstName,
          lastName,
          phone: phoneNumber,
          email,
          unitGroupId,
          ipAddress,
          googleClientId,
        },
        isGated: false,
        curious: false,
      }).then(setLeadConfirmed(true));
    }
  };

  return (
    <div className={styles.unitListingContainer}>
      {map(take(unitsToShow, 3), unitGroup => (
        <UnitListingRow
          key={unitGroup.id}
          unitGroup={unitGroup}
          facility={facility}
          moveInPath={moveInPath}
          toggleCreateLeadModal={toggleCreateLeadModal}
        />
      ))}

      {availableUnitGroups.length === 0 ? (
        <Alert context="warning">
          <Translation
            id="widgets.facilityList.unitFilter.noAvailableUnits"
            defaultMessage="No available unit groups"
          />
        </Alert>
      ) : null}

      {availableUnitGroups.length > 0
       && unitsToShow.length === 0 ? (
        <Alert context="warning">
          <Translation
            id="widgets.facilityList.unitFilter.noAvailableUnitsForFilter"
            defaultMessage="No available unit groups for the selected filters"
          />
        </Alert>
        ) : null}

      <div className={classNames(styles.unitGroupRow, 'row')}>
        <Col xs={12} lg={8} />
        <Col xs={12} lg={4}>
          <div className={styles.reserveButtonContainer}>
            <SMFacilityLink
              facility={facility}
              anchorTag="unitstable"
              multiLocale={multiLocale}
            >
              <Translation
                id="enterprise.widgets.nap.seeAvailableUnits"
                defaultMessage="See Available Units"
              />
            </SMFacilityLink>
          </div>
        </Col>
      </div>
      <CreateLeadModal
        show={showCreateLeadModal}
        unitGroup={
          showCreateLeadModal
            ? availableUnitGroups.find(grp => grp.id === selectedUnitGroupId)
            : undefined
        }
        onSubmit={handleLeadFormSubmit}
        onDismiss={toggleCreateLeadModal}
        confirmed={leadConfirmed}
        theme={theme}
        facility={facility}
      />
    </div>
  );
};

UnitListing.propTypes = propTypes;
UnitListing.defaultProps = defaultProps;

UnitListing.fragments = {
  facility: gql`
    fragment UnitGroupsFacility on Facility {
      currencySymbol
      phone
      address {
        state
      }
      settings {
        softwareProvider
      }
      unitGroups {
        id
        length
        width
        height
        area
        categoryName
        discountedPrice
        price
        availableUnitsCount
        promoId
        pricedWeekly
        leadOnly
        type
        amenities {
          token
        }
      }
    }
  `,
};

export default compose(
  connect(null, {
    createParkingLead: createLead,
  }),
)(UnitListing);
